import React, { useEffect, useState, useCallback } from 'react';
import { 
  View, 
  Text, 
  TouchableOpacity, 
  Image, 
  ScrollView, 
  StyleSheet,
} from 'react-native';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useNavigation, useRoute, NavigationProp, RouteProp } from '@react-navigation/native';
import { RootStackParamList } from './App.web'; 
import { Character, Story } from './types';
import { apiUrl } from './utils/config';
import { useStory } from './StoryContext';
import { useFocusEffect } from '@react-navigation/native';
import * as Font from 'expo-font';

const ExistingCharactersPage: React.FC = () => {
  const [characters, setCharacters] = useState<Character[]>([]);
  const [loading, setLoading] = useState(true);
  const [voice, setVoice] = useState('Joanna');
  //const [sessionId, setSessionId] = useState<string | null>(null);
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();
  const route = useRoute<RouteProp<RootStackParamList, 'ExistingCharactersPage'>>();

  const { context, setContext } = useStory();
  const [storyData, setStoryData] = useState<Story | null>(null);
  const [withImages, setWithImages] = useState<boolean>(false);
  const [sessionId, setSessionId] = useState<string | null>(null);

  const loadFonts = async () => {
        await Font.loadAsync({
          'Futura': require('./assets/fonts/futura.ttf'),
        });
      };

  useEffect(() => {
    if (context?.sessionId && context?.selectedStory) {
      setStoryData(context.selectedStory || route.params?.selectedStory || null);
      setWithImages(context.withImages || route.params?.withImages || false);
      setSessionId(context.sessionId || route.params?.sessionId || null);
      loadFonts();
    }
  }, [context, route.params]);


  const getAuthToken = useCallback(async (): Promise<any | null> => {
    try {
      const session = await fetchAuthSession();
      return session?.tokens?.idToken || null;
    } catch (error) {
      console.error('Error retrieving auth token:', error);
      return null;
    }
  }, []);

  const getUserID = async (): Promise<string | null> => {
      try {
        const session = await fetchAuthSession();
        const user_id = session?.tokens?.accessToken?.payload?.sub;
        return user_id || null;
      } catch (error) {
        console.error('Error retrieving user ID:', error);
        return null;
      }
    };

  const fetchSettings = useCallback(async () => {
    try {
      const authToken = await getAuthToken();
      const userId = await getUserID();
      if (!authToken) throw new Error('Unable to retrieve auth token');

      const response = await fetch(`${apiUrl}settings/get-user-settings?user_id=${userId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setVoice(data.voice || 'Joanna');
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  }, [apiUrl, getAuthToken]);

  const fetchCharacters = useCallback(async () => {
    try {
      const authToken = await getAuthToken();
      if (!authToken) throw new Error('Unable to retrieve auth token');
  
      const response = await fetch(`${apiUrl}characters/get-characters`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
      });
  
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
  
      const data = await response.json();
      setCharacters(data);
    } catch (error) {
      console.error('Error fetching characters:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      fetchCharacters();
    }, []) // Empty dependency array ensures it runs every time the page is focused
  );

  useEffect(() => {
    const loadSessionAndData = async () => {
      setLoading(true); // Start loading indicator
      try {
        const authToken = await getAuthToken();
  
        if (authToken && sessionId) {
          // Use Promise.all to fetch data concurrently
          await Promise.all([fetchCharacters(), fetchSettings()]);
        } else {
          console.warn('Auth token or session ID missing. Skipping data fetch.');
        }
      } catch (error) {
        console.error('Error loading session and data:', error);
      } finally {
        setLoading(false); // Stop loading indicator
      }
    };
  
    if (sessionId) {
      loadSessionAndData();
    } else {
      console.warn('Session ID is not available.');
    }
  }, [sessionId, fetchCharacters, fetchSettings, getAuthToken]);  
  

  const handleCardClick = (character: Character) => {
    navigation.navigate('CharacterDetailsPage', {
      character,
      selectedStory: storyData,
      voice,
      sessionId,
      withImages, // Pass the `withImages` value
    });
  };
  

  const handleCreateNewCharacter = () => {
    if (sessionId) {
      navigation.navigate('NewCharacterPage', {
        selectedStory: storyData,
        sessionId,
        withImages,
      });
    } else {
      console.error('Session ID is missing, unable to navigate.');
    }
  };

  // Utility function to truncate text
  const truncateText = (text: string | null | undefined, maxLength: number): string => {
    if (!text) {
      return ''; // Return a blank string if text is null or undefined
    }
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };
  

  const handleBack = () => {
    navigation.navigate('StoriesPage');
  };

  return (
    <View style={styles.container}>
      {loading ? (
        <View style={styles.loadingContainer}>
          {/* Make sure wizard.gif exists in the specified path */}
          <Image source={require('./assets/wizard.gif')} style={styles.loadingGif} />
        </View>
      ) : (
        <>
          <View style={styles.header}>
            <TouchableOpacity onPress={() => handleBack()}>
              <Image source={require('./assets/back-btn.png')} style={styles.backIcon} />
            </TouchableOpacity>
          </View>
          <Text style={styles.pageHeading}>Character Select</Text>
          <Text
            style={{
              textAlign: 'center',
              fontSize: 20,
              color: '#000',
              marginBottom: 16,
              fontFamily: 'Futura',
            }}
          >
            {characters.length}/10 Characters
          </Text>
          {characters.length < 10 && (
            <TouchableOpacity style={styles.createCharacterButton} onPress={handleCreateNewCharacter}>
              <View style={styles.createCharacterContent}>
                <Image source={require('./assets/plus-icon.png')} style={styles.plusIcon} />
                <Text style={styles.buttonText}>Create New Character</Text>
              </View>
            </TouchableOpacity>
          )}
          <ScrollView contentContainerStyle={styles.characterList}>
            {characters.map((character, index) => (
              <TouchableOpacity
                key={index}
                style={[
                  styles.characterCard,
                  { backgroundColor: index % 2 === 0 ? '#A39889' : '#AFC9BE' },
                ]}
                onPress={() => handleCardClick(character)}
              >
                <Image source={{ uri: character.image_url }} style={styles.characterImage} />
                <View style={styles.characterInfo}>
                  <Text style={styles.characterName}>{character.name}</Text>
                  <Text style={styles.characterDescription}>
                    {truncateText(character.description, 50)}
                  </Text>
                </View>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </>
      )}
    </View>
  );
  
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    paddingTop: 60,
    backgroundColor: '#F7F8FB',
  },
  loadingContainer: {
    ...StyleSheet.absoluteFillObject, // Fills the entire screen
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)', // Optional dim background
    zIndex: 10, // Ensure it overlays other content
  },
  loadingGif: {
    width: 150,
    height: 150,
    zIndex: 11, // Ensure it overlays other content
  },
  header: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1,
    height: 60, // Pick a height that matches your design
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#F7F8FB',
    paddingHorizontal: 16,
  },
  backIcon: {
    width: 24,
    height: 24,
  },
  pageHeading: {
    fontSize: 32,
    color: '#160014',
    marginVertical: 20,
    textAlign: 'center',
    fontFamily: 'Futura',
  },
  createCharacterButton: {
    backgroundColor: '#d1d7e0',
    borderRadius: 30,
    paddingVertical: 20,
    paddingHorizontal: 10,
    alignItems: 'center',
    alignSelf: 'center',
    marginBottom: 20,
    width: '100%',
    maxWidth: 500,
  },
  createCharacterContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  plusIcon: {
    width: 35,
    height: 35,
    marginRight: 10,
  },
  buttonText: {
    color: '#160014',
    fontSize: 16,
    fontWeight: 'bold',
    fontFamily: 'Futura',
  },
  characterList: {
    flexDirection: 'column',
    alignItems: 'center', // Center items horizontally
    justifyContent: 'center', // Center items vertically if needed
    width: '100%', // Ensure it spans the screen
    maxWidth: 500, // Limit the maximum width
    alignSelf: 'center', // Center the entire list itself within its parent
  },
  characterCard: {
    flexDirection: 'row',
    borderRadius: 15,
    marginBottom: 20,
    width: '100%', // Cards take full width of the container
    height: 150,
    alignSelf: 'center', // Ensure the card itself is centered within the container
  },
  
  characterImage: {
    width: 150,
    height: '100%',
    borderRadius: 15,
  },
  characterInfo: {
    flex: 1,
    paddingLeft: 20,
    justifyContent: 'center',
  },
  characterName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#160014',
    fontFamily: 'Futura',
  },
  characterDescription: {
    fontSize: 14,
    color: '#160014',
    fontFamily: 'Futura',
  },
});

export default ExistingCharactersPage;
